import { UUID } from "crypto";
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useError } from "./ErrorContext";
import { TokenResult, TokenResultSchema } from "../models/TokenResult";
import { z } from "zod";

interface AuthContextType {
  token: string | null;
  uuid: UUID | null;
  setUuid: (uuid: UUID | null) => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  maxMp3Length: number | null;
  userName: string | null;
  eventName: string | null;
  description: string | null;
  eventDate: Date | null;
  authorizedFetch: (url: string, options?: RequestInit) => Promise<Response>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [uuid, setUuidState] = useState<UUID | null>(null);
  const [tokenResult, setTokenResult] = useState<TokenResult | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { showError } = useError();

  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;

  const clearAuth = useCallback(() => {
    setUuidState(null);
    setTokenResult(null);
    localStorage.removeItem("recording-opportunity-uuid");
  }, []);

  const setUuid = useCallback(
    (newUuid: UUID | null) => {
      if (newUuid) {
        setUuidState(newUuid);
        localStorage.setItem("recording-opportunity-uuid", newUuid);
      } else {
        clearAuth();
      }
    },
    [clearAuth],
  );

  // Check for cached UUID on mount
  useEffect(() => {
    const cachedUuid = localStorage.getItem("recording-opportunity-uuid");
    if (cachedUuid) {
      setUuidState(cachedUuid as UUID);
    }
  }, []);

  // Fetch token when UUID changes
  useEffect(() => {
    if (!uuid) return;

    const abortController = new AbortController();

    const fetchToken = async () => {
      setIsLoading(true);

      try {
        const response = await fetch(
          `${API_BASE_URL}/auth/get?code=${uuid.toString()}`,
          { method: "GET", signal: abortController.signal },
        );

        if (response.ok) {
          const data = await response.json();
          const result = TokenResultSchema.parse(data);
          setTokenResult(result);
          return;
        }

        // Handle errors
        const errorMessage = (() => {
          switch (response.status) {
            case 404:
              return "UUID is not valid.";
            case 409:
              return "UUID already redeemed.";
            default:
              return response.status >= 400 && response.status < 500
                ? "UUID is not valid."
                : "Internal server error. Please contact Tom.";
          }
        })();

        showErrorRef.current(errorMessage);
        clearAuth();
      } catch (error) {
        if (error instanceof z.ZodError) {
          console.error("Invalid response format:", error.message);
          showErrorRef.current("Invalid server response.");
          clearAuth();
        } else if (error instanceof Error && error.name === "AbortError") {
          // Ignore abort errors
        } else {
          console.error("Network error:", error);
          showErrorRef.current("Internal server error. Please contact Tom.");
          clearAuth();
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchToken();

    return () => abortController.abort();
  }, [uuid, clearAuth]);

  const authorizedFetch = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      const makeRequest = (authToken: string | null) => {
        const headers = {
          ...options.headers,
          ...(authToken && { Authorization: `Bearer ${authToken}` }),
        };
        return fetch(url, { ...options, headers });
      };

      let response = await makeRequest(tokenResult?.token ?? null);

      // Token expired — try refresh
      if (response.status === 401 && uuid) {
        try {
          const tokenResponse = await fetch(
            `${API_BASE_URL}/auth/get?code=${uuid.toString()}`,
            { method: "GET" },
          );

          if (tokenResponse.ok) {
            const data = await tokenResponse.json();
            const result = TokenResultSchema.parse(data);
            setTokenResult(result);
            response = await makeRequest(result.token);
          } else {
            showErrorRef.current("Session expired. Please try again.");
            clearAuth();
          }
        } catch (error) {
          console.error("Error refreshing token:", error);
          showErrorRef.current("Session expired. Please try again.");
          clearAuth();
        }
      }

      return response;
    },
    [uuid, tokenResult?.token, clearAuth],
  );

  return (
    <AuthContext.Provider
      value={{
        uuid,
        token: tokenResult?.token ?? null,
        setUuid,
        isAuthenticated: !!tokenResult?.token,
        isLoading,
        maxMp3Length: tokenResult?.maxMp3Length ?? null,
        userName: tokenResult?.userName ?? null,
        eventName: tokenResult?.eventName ?? null,
        description: tokenResult?.description ?? null,
        eventDate: tokenResult?.eventDate ?? null,
        authorizedFetch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
